import { motion } from "framer-motion";
import {
  LayoutGrid, Users, FileCheck, Settings, BarChart3, Search, Bell,
  ShieldCheck, Activity, ArrowUpRight,
} from "lucide-react";

const BARS = [42, 68, 55, 80, 62, 90, 74, 58, 84, 66, 95, 78];

export default function HeroDashboard() {
  return (
    <div className="relative" data-testid="hero-dashboard-mockup">
      {/* spotlight */}
      <div className="absolute -inset-10 rounded-full bg-blue-600/20 blur-3xl" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 40, rotateX: 8 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_40px_80px_-20px_rgba(2,8,23,0.6)]"
      >
        {/* window bar */}
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 hidden rounded-md bg-white px-3 py-1 font-mono-alt text-[10px] text-slate-400 ring-1 ring-slate-200 sm:block">
            portal.yourbusiness.co.uk
          </span>
        </div>

        <div className="grid grid-cols-[52px_1fr] sm:grid-cols-[160px_1fr]">
          {/* sidebar */}
          <div className="border-r border-slate-100 bg-slate-50/60 p-3 sm:p-4">
            <div className="hidden rounded-lg bg-[#0B1F3A] px-3 py-2 font-display text-[11px] font-semibold text-white sm:block">
              Client Portal
            </div>
            <nav className="mt-3 space-y-1" aria-hidden="true">
              {[
                { icon: LayoutGrid, label: "Overview", active: true },
                { icon: FileCheck, label: "Documents" },
                { icon: Users, label: "Customers" },
                { icon: BarChart3, label: "Reports" },
                { icon: Settings, label: "Settings" },
              ].map(({ icon: Icon, label, active }) => (
                <div
                  key={label}
                  className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] font-medium ${
                    active ? "bg-blue-600/10 text-blue-700" : "text-slate-400"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline">{label}</span>
                </div>
              ))}
            </nav>
          </div>

          {/* main panel */}
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-sm font-semibold text-[#0B1F3A]">Dashboard</p>
                <p className="text-[10px] text-slate-400">Last 12 months overview</p>
              </div>
              <div className="flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-slate-300" />
                <span className="relative">
                  <Bell className="h-3.5 w-3.5 text-slate-300" />
                  <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-blue-600" />
                </span>
              </div>
            </div>

            {/* KPI cards */}
            <div className="mt-4 grid grid-cols-3 gap-2.5">
              {[
                { label: "Active users", value: "1,284", trend: "+12%" },
                { label: "Requests", value: "8,430", trend: "+8%" },
                { label: "Avg. response", value: "0.8s", trend: "-24%" },
              ].map((kpi) => (
                <div key={kpi.label} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5 sm:p-3">
                  <p className="truncate text-[9px] font-medium uppercase tracking-wide text-slate-400">{kpi.label}</p>
                  <p className="mt-1 font-mono-alt text-sm font-bold text-[#0B1F3A] sm:text-base">{kpi.value}</p>
                  <p className="text-[9px] font-semibold text-emerald-600">{kpi.trend}</p>
                </div>
              ))}
            </div>

            {/* chart */}
            <div className="mt-3 rounded-xl border border-slate-100 p-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold text-slate-500">Monthly activity</p>
                <span className="rounded-full bg-blue-600/10 px-2 py-0.5 text-[9px] font-semibold text-blue-700">Live</span>
              </div>
              <div className="mt-3 flex h-16 items-end gap-1.5 sm:h-20" aria-hidden="true">
                {BARS.map((h, i) => (
                  <motion.span
                    key={i}
                    className="flex-1 rounded-t-md bg-gradient-to-t from-blue-600 to-blue-400"
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ duration: 0.9, delay: 0.9 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  />
                ))}
              </div>
            </div>

            {/* rows */}
            <div className="mt-3 space-y-2" aria-hidden="true">
              {[
                { w: "w-3/4", tag: "Invoice approved" },
                { w: "w-1/2", tag: "Document signed" },
              ].map((r) => (
                <div key={r.tag} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                  <span className="text-[10px] font-medium text-slate-500">{r.tag}</span>
                  <span className={`ml-auto h-1.5 ${r.w} rounded-full bg-slate-200`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* floating badges */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, duration: 0.5 }}
        className="float-slow absolute -right-3 -top-5 flex items-center gap-2 rounded-xl border border-white/10 bg-[#0F284B]/95 px-3.5 py-2.5 shadow-xl backdrop-blur sm:-right-6"
      >
        <ShieldCheck className="h-4 w-4 text-emerald-400" aria-hidden="true" />
        <span className="text-xs font-semibold text-white">SSL Secured</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        className="float-slow absolute -bottom-5 -left-3 flex items-center gap-2 rounded-xl border border-white/10 bg-[#0F284B]/95 px-3.5 py-2.5 shadow-xl backdrop-blur sm:-left-8"
      >
        <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
        <span className="text-xs font-semibold text-white">All systems operational</span>
        <Activity className="h-3.5 w-3.5 text-blue-400" aria-hidden="true" />
      </motion.div>
    </div>
  );
}
